import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BankAccountsListResponse,
  BankAccountDetailResponse,
  CreateBankAccountRequest,
  UpdateBankAccountRequest,
} from '@stfors/shared-types';
import { api } from '../client';

// Company bank accounts are admin-managed metadata — cache aggressively but
// invalidate on every mutation so a fresh add shows up in the payment picker.
const STALE_MS = 5 * 60 * 1000;

export function useBankAccounts(activeOnly = false) {
  const qs = activeOnly ? '?active=1' : '';
  return useQuery({
    queryKey: ['bank-accounts', { activeOnly }],
    queryFn: ({ signal }) =>
      api.get<BankAccountsListResponse>(`/api/bank-accounts${qs}`, signal),
    staleTime: STALE_MS,
  });
}

export function useCreateBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBankAccountRequest) =>
      api.post<BankAccountDetailResponse>('/api/bank-accounts', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
  });
}

export interface UpdateBankAccountInput extends UpdateBankAccountRequest {
  id: number;
}

export function useUpdateBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateBankAccountInput) =>
      api.patch<BankAccountDetailResponse>(`/api/bank-accounts/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
  });
}

export function useDeleteBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/bank-accounts/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
  });
}
